import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Row, Col, Dropdown, Button, Tooltip, Spin, Input } from 'antd';
import PropTypes from 'prop-types';

import { getUserId } from 'graphql/cache';
import { FETCH_PIPEDRIVE_DEALS } from 'graphql/queries/integrationQueries';
import images from 'constants/images';
import AddRoundIcon from 'components/Icons/AddRoundIcon';
import InfoIcon from 'components/Icons/InfoIcon';

import './PipedriveDealName.scss';

/**
 * PipedriveDealName Component
 *
 * A dropdown component for selecting Pipedrive deals with pagination, search, and keyboard navigation.
 * Replaces the original Ant Design Cascader with enhanced functionality including:
 * - Cursor-based pagination with infinite scroll
 * - Real-time search with intelligent sorting
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Auto-focus on search input
 * - Loading states and error handling
 */
const PipedriveDealName = ({
  label,
  type,
  action,
  onChange,
  dealNameValue,
  language,
  fromTemplate,
}) => {
  // Ref for the main container
  const pipedriveDealNameRef = useRef(null);

  const [dealName, setDealName] = useState();
  const [deals, setDeals] = useState([]);
  const [visiblePipedriveDealName, setVisiblePipedriveDealName] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const searchInputRef = useRef(null);

  // GraphQL query for fetching Pipedrive deals with pagination
  const [fetchPipedriveDeals] = useLazyQuery(FETCH_PIPEDRIVE_DEALS, {
    skip: !getUserId(), // Skip if user is not logged in
    fetchPolicy: 'network-only', // Always fetch from network, not cache
    onCompleted: ({ fetchPipedriveDeals }) => {
      const newDeals = fetchPipedriveDeals.deals || [];
      const newNextCursor = fetchPipedriveDeals.nextCursor;

      if (nextCursor) {
        // Append new deals to existing ones for pagination
        setDeals((prevDeals) => [...prevDeals, ...newDeals]);
      } else {
        // First load - replace deals and set initial deal name if provided
        setDeals(newDeals);
        setDealName(newDeals.find((deal) => deal.id === dealNameValue)?.title);
      }

      setNextCursor(newNextCursor);
      setHasMore(!!newNextCursor); // Set hasMore based on whether nextCursor exists
      setLoading(false);
    },
    onError: (error) => {
      console.log(error);
      setLoading(false);
    },
  });

  // Fetch initial deals when component mounts
  useEffect(() => {
    fetchPipedriveDeals({
      variables: {
        cursor: null, // Start from the beginning
        limit: 50, // Fetch 50 deals initially
      },
    });
  }, []); // eslint-disable-line

  /**
   * Load more deals for pagination
   * Only executes if not currently loading, has more data, and has a next cursor
   */
  const loadMoreDeals = useCallback(() => {
    if (loading || !hasMore || !nextCursor) return;

    setLoading(true);
    fetchPipedriveDeals({
      variables: {
        cursor: nextCursor, // Use the cursor from previous response
        limit: 50, // Fetch 50 more deals
      },
    });
  }, [fetchPipedriveDeals, loading, hasMore, nextCursor]);

  /**
   * Handle deal selection or new deal creation
   * @param {string} name - The deal name/title
   * @param {string|number} id - The deal ID
   */
  const onInputChange = useCallback(
    (name, id) => {
      setDealName(name);
      setSelectedDealId(id); // Set the selected deal ID
      setVisiblePipedriveDealName(false);

      if (fromTemplate) {
        // Template mode: pass all parameters for template handling
        onChange('', '', '', '', name, id);
      } else {
        // Normal mode: handle deal-specific actions
        if (action === 'deal') {
          onChange({ type: 'setDeal', action: 'deal', value: id || '' });
        }
        onChange({ type, action, value: id });
      }
    },
    [onChange, fromTemplate, action, type]
  );

  // Filter and sort deals based on search term
  const filteredDeals = deals
    .filter((deal) => deal.title.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (!searchTerm) return 0;

      // Prioritize exact matches over partial matches
      const aExact = a.title.toLowerCase() === searchTerm.toLowerCase();
      const bExact = b.title.toLowerCase() === searchTerm.toLowerCase();

      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      return 0;
    });

  /**
   * Handle scroll events for infinite scroll pagination
   * Triggers loading more deals when user scrolls near the bottom
   */
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Load more when user scrolls to bottom (with 50px threshold for better UX)
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreDeals();
      }
    },
    [loadMoreDeals]
  );

  /**
   * Handle keyboard navigation within the dropdown
   * Supports arrow keys for navigation, enter for selection, and escape to close
   */
  const handleKeyDown = useCallback(
    (event) => {
      if (!visiblePipedriveDealName) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          // Move selection down, but don't go beyond the last item
          setSelectedIndex((prev) => (prev < filteredDeals.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          event.preventDefault();
          // Move selection up, allow going back to -1 (no selection)
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          event.preventDefault();
          // Select the currently highlighted deal
          if (selectedIndex >= 0 && selectedIndex < filteredDeals.length) {
            const selectedDeal = filteredDeals[selectedIndex];
            onInputChange(selectedDeal.title, selectedDeal.id);
          }
          break;
        case 'Escape':
          event.preventDefault();
          // Close the dropdown
          setVisiblePipedriveDealName(false);
          break;
        default:
          break;
      }
    },
    [visiblePipedriveDealName, selectedIndex, filteredDeals, onInputChange]
  );

  // Reset selected index when search term changes to prevent invalid selections
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  // Custom dropdown menu with search, pagination, and keyboard navigation
  const menu = (
    <div
      className={`wix-client-name-dropdown-menu pipedrive-deal-dropdown ${
        language === 'hebrew' || language === 'arabic' ? 'language-rtl' : ''
      }`}
      onScroll={handleScroll} // Enable infinite scroll
      onKeyDown={handleKeyDown} // Enable keyboard navigation
      tabIndex={0} // Make the dropdown focusable for keyboard events
    >
      {/* Search input for filtering deals */}
      <div className="search-container">
        <Input
          ref={searchInputRef} // Ref for auto-focus functionality
          placeholder="Search deals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          allowClear // Allow clearing the search input
        />
      </div>

      {/* Render filtered deals with keyboard navigation support */}
      {filteredDeals?.map((deal, index) => (
        <div
          key={deal.id}
          className={`deal-option ${deal.id === selectedDealId ? 'selected' : ''}`}
          onClick={() => onInputChange(deal.title, deal.id)} // Handle deal selection
        >
          {deal.title}
        </div>
      ))}

      {/* Loading indicator for pagination */}
      {loading && (
        <div className="loading-container">
          <Spin size="small" /> Loading more deals...
        </div>
      )}

      {/* Message when all deals have been loaded */}
      {!hasMore && deals.length > 0 && filteredDeals.length === deals.length && (
        <div className="no-more-deals">No more deals to load</div>
      )}

      {/* Message when no deals are found (either no data or no search results) */}
      {filteredDeals.length === 0 && !loading && (
        <div className="no-deals-found">
          {searchTerm ? 'No deals match your search' : 'No deals found'}
        </div>
      )}
    </div>
  );

  return (
    <div className="wix-client-name-dropdown-container" ref={pipedriveDealNameRef}>
      {/* Label and tooltip section */}
      {label && (
        <Row>
          <label className="input-label">{label}</label>
          <Tooltip
            overlayClassName="wizard-tooltip-email wizard-tooltip-deal"
            title="Search for a Pipedrive deal"
            color="black"
            key="white">
            <InfoIcon />
          </Tooltip>
        </Row>
      )}

      {/* Main dropdown component */}
      <Dropdown
        overlay={menu} // Custom menu with search and pagination
        trigger={['click']} // Open on click only
        visible={visiblePipedriveDealName}
        onVisibleChange={(visible) => {
          setVisiblePipedriveDealName(visible);
          if (!visible) {
            // Reset state when dropdown closes
            setSearchTerm('');
            setSelectedIndex(-1);
          } else {
            // Reset selection when dropdown opens
            setSelectedIndex(-1);
            // Auto-focus search input when dropdown opens (with small delay for smooth UX)
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 100);
          }
        }}
        placement="bottomLeft"
        getPopupContainer={(trigger) => trigger.parentElement} // Ensure proper positioning
      >
        {/* Dropdown trigger button */}
        <Button
          id="wix-client-name-dropdown-button"
          onClick={() => setVisiblePipedriveDealName(!visiblePipedriveDealName)}>
          <Row justify="space-between">
            <Col>
              {/* Pipedrive deal icon */}
              <img
                alt="Pipedrive"
                className={fromTemplate ? 'wix-template' : ''}
                src={images.PIPEDRIVE_DEAL_ICON}
              />
              {/* Display selected deal name or placeholder text */}
              <span className={`wix-button-text ${fromTemplate ? 'wix-template' : ''}`}>
                {dealName || 'Select Deal'}
              </span>
            </Col>
            <Col>
              {/* Add icon to indicate dropdown functionality */}
              <AddRoundIcon />
            </Col>
          </Row>
        </Button>
      </Dropdown>
    </div>
  );
};

// Default props for the component
PipedriveDealName.defaultProps = {
  dealNameValue: '', // Default empty string for deal name value
};

// PropTypes for type checking and documentation
PipedriveDealName.propTypes = {
  type: PropTypes.string.isRequired, // Type of action (e.g., 'setDeal')
  action: PropTypes.string.isRequired, // Action to perform (e.g., 'deal')
  onChange: PropTypes.func.isRequired, // Callback function for value changes
  dealNameValue: PropTypes.string, // Initial deal name value
  language: PropTypes.string, // Language for RTL support
};

export default PipedriveDealName;
